import { trpc } from "../lib/trpc.js";
import { Globe, Plus, ExternalLink, TrendingUp, Users, DollarSign, Loader2 } from "lucide-react";

const statusConfig: Record<string, { color: string; label: string; dot: string }> = {
  active:   { color: "bg-success/10 text-success border-success/20",   label: "Ativo",        dot: "bg-success" },
  paused:   { color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", label: "Pausado",      dot: "bg-yellow-400" },
  building: { color: "bg-primary/10 text-primary border-primary/20",          label: "Construindo",  dot: "bg-primary animate-pulse" },
  error:    { color: "bg-destructive/10 text-destructive border-destructive/20", label: "Erro",       dot: "bg-destructive" },
};

export function Sites() {
  const { data, isLoading } = trpc.sites.list.useQuery();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
            <Globe className="w-6 h-6 text-primary" />
            Sites
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie seus sites de nicho e acompanhe a performance
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 shadow-lg shadow-primary/20">
          <Plus className="w-4 h-4" />
          Novo Site
        </button>
      </div>

      {/* Site Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-6 animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-muted rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-muted rounded w-32 mb-2" />
                  <div className="h-3 bg-muted rounded w-24" />
                </div>
              </div>
              <div className="h-3 bg-muted rounded w-full mb-2" />
              <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-border">
                <div className="h-8 bg-muted rounded" />
                <div className="h-8 bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : data?.items && data.items.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((site) => {
            const cfg = statusConfig[site.status] || statusConfig.error;
            return (
              <div
                key={site.id}
                className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 transition-all duration-200 group"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
                      <Globe className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground text-sm truncate">{site.name}</h3>
                      {site.niche && (
                        <p className="text-[11px] text-muted-foreground truncate">{site.niche}</p>
                      )}
                    </div>
                  </div>
                  <span className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border font-medium shrink-0 ${cfg.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                    {cfg.label}
                  </span>
                </div>

                {/* Domain */}
                {site.domain && (
                  <a
                    href={`https://${site.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors mb-3 group/link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                    {site.domain}
                  </a>
                )}

                {/* Metrics */}
                <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border">
                  <div className="bg-secondary/60 rounded-lg p-2.5">
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
                      <DollarSign className="w-3 h-3" />
                      Receita
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      ${((site.revenue || 0) / 100).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-secondary/60 rounded-lg p-2.5">
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
                      <Users className="w-3 h-3" />
                      Tráfego
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      {(site.traffic || 0).toLocaleString("pt-BR")}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-16 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Globe className="w-8 h-8 text-primary opacity-60" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum site criado ainda</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
            Crie seu primeiro site de nicho para começar a monetizar com tráfego de arbitragem.
          </p>
          <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 shadow-lg shadow-primary/20">
            <Plus className="w-4 h-4" />
            Criar Primeiro Site
          </button>
        </div>
      )}
    </div>
  );
}
